import React, { useEffect, useRef } from "react";
import { Alert, Button, ComboboxItemGroup, Container, Divider, Group, MultiSelect, NumberInput, Paper, ScrollArea, Select, Space, Stack, Text, TextInput, Textarea, ThemeIcon, Title, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertTriangle, IconCheck, IconChevronLeft, IconLock, IconMapPin, IconTag, IconTrash, IconUser } from "@tabler/icons-react";

import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../RoleTemplate";
import { useAccess } from "../../Auth/auth-functions";
import { useGetLocationsQuery } from "../../api/location.api";
import { useGetCategoriesQuery } from "../../api/category.api";
import { useGetUsersQuery } from "../../api/user.api";
import { useGetTeamsQuery } from "../../api/team.api";
import { useGetAssetsQuery } from "../../api/asset.api";
import { useGetVendorsQuery } from "../../api/vendor.api";
import { WorkOrderTemplateDto, MutableWorkOrderTemplateDto } from "../../models/workordertemplate.models";
import { PriorityInput } from "../WorkOrders/PriorityInput";
import classes from "./WorkOrderTemplateDetailView.module.css";

interface WorkOrderTemplateEditViewProps {
	template?: WorkOrderTemplateDto | null;
	onSave: (values: MutableWorkOrderTemplateDto) => void;
	onClose: () => void;
	isSaving?: boolean;
	onChanged?: (changed: boolean) => void;
}

export const WorkOrderTemplateEditView: React.FC<WorkOrderTemplateEditViewProps> = ({
	template,
	onSave,
	onClose,
	isSaving = false,
	onChanged,
}) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const { data: locations } = useGetLocationsQuery();
	const { data: categories } = useGetCategoriesQuery();
	const { data: users } = useGetUsersQuery();
	const { data: teams } = useGetTeamsQuery();
	const { data: assets } = useGetAssetsQuery();
	const { data: vendors } = useGetVendorsQuery();

	const locationItems = locations?.map(location => ({ value: location.LocationId, label: location.Title })) || [];
	const categoryItems = categories?.map(category => ({ value: category.CategoryId, label: category.Title })) || [];
	const assetItems = assets?.map(asset => ({ value: asset.AssetId, label: `${asset.Name}${asset.AssetTag ? ` (${asset.AssetTag})` : ''}` })) || [];
	const vendorItems = vendors?.map(vendor => ({ value: vendor.VendorId, label: vendor.Name })) || [];
	const userItems = users?.filter(user => user.Deleted == false).map(user => ({ value: user.UserId, label: user.Name })) || [];
	const teamItems = teams?.filter(team => team.Deleted == false).map(team => ({ value: team.TeamId, label: team.Name })) || [];

	const assignmentItems: ComboboxItemGroup[] = [
		{ group: "Users", items: userItems },
		{ group: "Teams", items: teamItems },
	];

	const isNew = !template || template.WorkOrderTemplateId === "new";
	const canEditTemplate = useAccess(AzureADRoles.Admin);

	const form = useForm<MutableWorkOrderTemplateDto>({
		initialValues: {
			WorkOrderTemplateId: template?.WorkOrderTemplateId || "",
			Title: template?.Title || "",
			Description: template?.Description || "",
			Priority: template?.Priority.PriorityId || "",
			Location: template?.Location.LocationId || "",
			Categories: template?.Categories.map(category => category.CategoryId) || [],
			Assets: template?.Assets.map(asset => asset.AssetId) || [],
			Vendors: template?.Vendors.map(vendor => vendor.VendorId) || [],
			AssignedTo: [...template?.AssignedTo?.Users.map(user => user.UserId) || [],
				...template?.AssignedTo?.Teams.map(team => team.TeamId) || []
			],
			DueDays: template?.DueDays || null,
		},
		validate: {
			Title: (value) => (value.length < 3 ? "Title must be at least 3 characters long" : null),
			Priority: (value) => (!value ? "Priority is required" : null),
			Location: (value) => (!value ? "Location is required" : null),
			Categories: (value) => (value.length === 0 ? "At least one category is required" : null),
			AssignedTo: (value) => (value && value.length === 0 ? "At least one user or team must be assigned" : null),
		},
	});

	// Track dirty state and call onChanged
	const prevDirty = useRef(form.isDirty());
	useEffect(() => {
		if (onChanged && prevDirty.current !== form.isDirty() && canEditTemplate) {
			onChanged(form.isDirty());
			prevDirty.current = form.isDirty();
		}
	}, [form.isDirty(), onChanged, canEditTemplate]);

	useEffect(() => {
		form.setValues({
			Title: template?.Title || "",
			Description: template?.Description || "",
			Priority: template?.Priority.PriorityId || "",
			Location: template?.Location.LocationId || "",
			Categories: template?.Categories.map(category => category.CategoryId) || [],
			Assets: template?.Assets.map(asset => asset.AssetId) || [],
			Vendors: template?.Vendors.map(vendor => vendor.VendorId) || [],
			AssignedTo: [...template?.AssignedTo?.Users.map(user => user.UserId) || [],
				...template?.AssignedTo?.Teams.map(team => team.TeamId) || []
			],
			DueDays: template?.DueDays || null,
		});

		if (isNew)
			form.setDirty({ "Title": true });
		// eslint-disable-next-line
	}, [template]);

	const handleSave = (values: typeof form.values) => {
		if (!form.isValid()) {
			form.validate();
			return;
		}
		onSave(values);
	};

	if (template?.Deleted != null) {
		return (
			<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
				<Stack gap="xs" mt="xl" justify="center" align="center">
					<ThemeIcon size={80} radius={80} color="red" variant="light">
						<IconTrash size={40} />
					</ThemeIcon>
					<Text fz="xl" fw="bold">
						Template Deleted
					</Text>
					<Text ta="center">This template has been deleted and cannot be edited.</Text>
				</Stack>
			</Paper>
		);
	}

	const noAccess = (
		<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
			<Stack gap="xs" mt="xl" justify="center" align="center">
				<ThemeIcon size={80} radius={80} color="gray" variant="light">
					<IconLock size={40} />
				</ThemeIcon>
				<Text fz="xl" fw="bold">
					No Access
				</Text>
				<Text ta="center">You do not have permission to create or edit work order templates. Please contact your administrator.</Text>
			</Stack>
		</Paper>
	);

	return <RoleTemplate role={AzureADRoles.Admin} noAccess={noAccess}>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<Button variant="subtle" radius="xl" ml={-15} onClick={onClose} miw={100} leftSection={<IconChevronLeft size={20} />}>
								Back
							</Button>
							<Title order={isMobile ? 6 : 3}>{template ? "Edit Template" : "New Template"}</Title>
						</Group>
						<Button
							onClick={() => handleSave(form.values)}
							disabled={!form.isDirty()}
							loading={isSaving}
							variant="filled"
							radius="xl"
							miw={100}
							leftSection={<IconCheck />}
						>
							Save
						</Button>
					</Group>
				</Stack>
				<Container px="20">
					{form.errors && Object.keys(form.errors).length > 0 && <>
						<Alert title="Please fix the following errors:" color="red" mb="md" radius="lg">
							<Stack gap={0} mb={0}>
								{Object.entries(form.errors).map(([field, error]) => error && 
									<Group gap={5} key={field}>
										<ThemeIcon c="red" w={20} variant="transparent" m={-5}><IconAlertTriangle size={14} /></ThemeIcon>
										<Text key={field} fz="sm">{error}</Text>
									</Group>
								)}
							</Stack>
						</Alert>
						<Divider mb="md" />
					</>}
					<form onSubmit={form.onSubmit((values) => handleSave(values))}>
						<Button type="submit" style={{ display: 'none' }} />
						<TextInput
							label="Template Name"
							key={form.key("Title")}
							{...form.getInputProps("Title")}
							placeholder="Enter template title"
							maxLength={255}
							required
							mb="xl"
						/>

						<Textarea
							label="Description"
							key={form.key("Description")}
							{...form.getInputProps("Description")}
							placeholder="Enter template description"
							mb="xl"
							autosize
							minRows={3}
						/>

						<PriorityInput
							label="Priority"
							value={form.values.Priority}
							onChange={(value) => form.setFieldValue('Priority', value)}
							error={form.errors.Priority}
							mb="xl"
						/>

						<MultiSelect label="Assigned To"
							key={form.key("AssignedTo")}
							data={assignmentItems}
							{...form.getInputProps("AssignedTo")}
							placeholder="Select users or teams"
							checkIconPosition="right"
							required
							searchable
							leftSection={<IconUser size={16} />}
							mb="xl"
						/>

						<Select label="Location"
							key={form.key("Location")}
							{...form.getInputProps("Location")}
							data={locationItems}
							placeholder="Select a location"
							checkIconPosition="left"
							required
							searchable
							leftSection={<IconMapPin size={16} />}
							mb="xl"
						/>

						<MultiSelect label="Categories"
							key={form.key("Categories")}
							{...form.getInputProps("Categories")}
							data={categoryItems}
							placeholder="Select categories"
							checkIconPosition="right"
							required
							searchable
							leftSection={<IconTag size={16} />}
							mb="xl"
						/>

						<MultiSelect label="Assets"
							key={form.key("Assets")}
							{...form.getInputProps("Assets")}
							data={assetItems}
							placeholder="Select assets"
							checkIconPosition="right"
							searchable
							mb="xl"
						/>

						<MultiSelect label="Vendors"
							key={form.key("Vendors")}
							{...form.getInputProps("Vendors")}
							data={vendorItems}
							placeholder="Select vendors"
							checkIconPosition="right"
							searchable
							mb="xl"
						/>

						<NumberInput
							label="Due Days (optional)"
							key={form.key("DueDays")}
							{...form.getInputProps("DueDays")}
							placeholder="Number of days until due"
							min={1}
							max={365}
							maw={210}
							mb="xl"
						/>

						<Space h="xl" />
					</form>
				</Container>
			</ScrollArea>
		</Paper>
	</RoleTemplate>
}
